"use client";

import { ComponentType, FC, useEffect, useMemo, useState } from "react";
import OpenDematButton from "./DematButton";
import Conditional from "@repo/shared-ui/Conditional";
import BFLModal from "@repo/shared-ui/BFLModal";
import dynamic from "next/dynamic";
import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import {
  useAppSelector,
  selectIsAuthSuccess,
  selectIsAuthSuccessOrPerpetual,
  selectLoginStatus,
  selectLeadStatus,
} from "@repo/web-store";
import {
  handleDematAccountDL,
  handleClickDL,
  handleViewDL,
} from "./DematAccountFunction";
import {
  authenticationConsentCrossIconCtaText,
  authenticationConsentCrossIconInteraction,
} from "../../pages/News/newsTrackingConstants";
import {
  trackAuthenticationConsentDrawerClick,
  trackDematCtaGroupEvent,
} from "./unifiedCt";
import { consentInsert, consentRead } from "@repo/sso/consentApi";
import { getSessionData } from "@repo/shared-utils/common-utils";
import {
  buildBfslEncryptedRedirectUrl,
  redirectWithUtmParams,
} from "./DematUtils";
import { BFSL_PWA_URL } from "./DematConstant";
import { ssoRedirect } from "@repo/sso/redirect-utils";
import { ICompany } from "../News/components/CompanyTag";
import CompanyBuySellModal from "./CompanyBuySellModal";
import useModalCloseOnBrowserButton from "@repo/shared-ui/useModalCloseOnBrowserButton";

const DematConsentModal: ComponentType = dynamic(
  () => import("./DematConsentModal.tsx") as any,
  { ssr: false, loading: () => <BFLSkeleton count={1} height={100} /> },
);

interface DematAccountProps {
  openDematButtonText?: string;
  resumeJourneyText?: string;
  compName: string;
  exchId: string;
  formattedSymbol: string;
  nseToken: number;
  bseToken: number;
  isFloating?: boolean;
  showArrow?: boolean;
  showTradeNow?: boolean;
  tradeNowText?: string;
  showBuySellButtons?: boolean;
  buyText?: string;
  sellText?: string;
  headerName?: string;
  companies?: ICompany[];
  modalTitle?: string;
  onMasterCtaClick?: (ctaText: string) => void;
  showUnifiedCt?: boolean;
  showCtaPadding?: boolean;
  /** AEM-authored BFSL PWA base URL; falls back to default when absent. */
  bfslPwaBaseUrl?: string;
  /** AEM-authored utm_source for app redirects. */
  utmSourceApp?: string;
  /** AEM-authored utm_source for web redirects. */
  utmSourceWeb?: string;
}

export const handleSSORedirect = async (
  sectionTitle: string,
  loginStatus: string,
  exchId: string,
  formattedSymbol: string,
  nseToken: number,
  bseToken: number,
  headerName: string = "Bajaj Broking",
) => {
  const { mobileNo } = (await getSessionData()) || {};
  const ssoParams = {
    buySellRedirectUrl: process.env.NEXT_PUBLIC_BFSL_DOMAIN,
    tokenExchId: exchId,
    sectionTitle: sectionTitle.toLocaleUpperCase() ?? "",
    headerName: headerName,
  };

  return ssoRedirect(
    {
      href: ssoParams?.buySellRedirectUrl ?? "",
      data: {
        screen: "orderpad",
        token: exchId === "0" ? nseToken : bseToken,
        symbol: formattedSymbol,
        action: sectionTitle,
        mobileNo: mobileNo,
        headerName: headerName,
      },
      target: "",
      checkCart: false,
      showResumePopup: false,
    },
    loginStatus,
  );
};

export const handleTradeNowRedirect = async (
  loginStatus: string,
  headerName: string = "Bajaj Broking",
) => {
  const { mobileNo } = (await getSessionData()) || {};
  const ssoParams = {
    buySellRedirectUrl: process.env.NEXT_PUBLIC_BFSL_DOMAIN,
    headerName: headerName,
  };

  return ssoRedirect(
    {
      href: ssoParams?.buySellRedirectUrl ?? "",
      data: {
        screen: "dashboard",
        mobileNo: mobileNo,
        headerName: headerName,
      },
      target: "",
      checkCart: false,
      showResumePopup: false,
    },
    loginStatus,
  );
};

const DematAccount: FC<DematAccountProps> = ({
  openDematButtonText,
  resumeJourneyText,
  compName,
  nseToken,
  bseToken,
  exchId,
  formattedSymbol,
  isFloating = false,
  showArrow = true,
  showTradeNow,
  tradeNowText,
  showBuySellButtons,
  buyText,
  sellText,
  headerName = "Bajaj Broking",
  companies = [],
  modalTitle,
  onMasterCtaClick,
  showUnifiedCt = false,
  showCtaPadding,
  bfslPwaBaseUrl,
  utmSourceApp,
  utmSourceWeb,
}) => {
  const [openModal, setOpenModal] = useState<"consent" | "none">("none");
  const [consentProvided, setConsentProvided] = useState<boolean>(false);
  const [buttonText, setButtonText] = useState<string | undefined>(
    openDematButtonText,
  );
  const [sessionMobileNo, setSessionMobileNo] = useState<string | undefined>(
    undefined,
  );
  const [openCompanyBuySellModal, setOpenCompanyBuySellModal] =
    useState<boolean>(false);
  const loginStatus = useAppSelector(selectLoginStatus);
  const leadStatus = useAppSelector(selectLeadStatus);
  const isAuthSuccess = useAppSelector(selectIsAuthSuccess);
  const isAuthSuccessOrPerpetual = useAppSelector(
    selectIsAuthSuccessOrPerpetual,
  );
  const handleCloseModal = () => {
    setOpenModal("none");
  };
  useModalCloseOnBrowserButton(openModal !== "none", handleCloseModal);

  // Get mobile number from session data
  useEffect(() => {
    let isMounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const fetchSessionMobileNumber = async (retryCount = 0) => {
      try {
        const { mobileNo } = await getSessionData();
        if (mobileNo) {
          if (isMounted) setSessionMobileNo(mobileNo);
        } else if (retryCount < 10 && isMounted) {
          timeoutId = setTimeout(
            () => fetchSessionMobileNumber(retryCount + 1),
            500,
          );
        }
      } catch (error) {
        if (retryCount < 10 && isMounted) {
          timeoutId = setTimeout(
            () => fetchSessionMobileNumber(retryCount + 1),
            500,
          );
        } else {
          console.error("Error fetching session data:", error);
        }
      }
    };

    fetchSessionMobileNumber();

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const submitConsentInsert = async () => {
    const consentResponse: any = await consentInsert(
      "BFSL",
      "BFL Entity",
      true,
    );
    return consentResponse;
  };

  /**
   * getConsentReadData
   */
  const getConsentReadData = async () => {
    const consentReadData = await consentRead("BFSL", "34", "BFL Entity");
    if (consentReadData?.data) {
      if (consentReadData?.data?.consentValue === "2") {
        setConsentProvided(true);
      }
    }
  };

  useEffect(() => {
    if (leadStatus === null) {
      console.warn("Lead status is null");
      return;
    }
    getConsentReadData();
    if (leadStatus === "Inprogress") {
      setButtonText(resumeJourneyText ?? "RESUME JOURNEY");
    } else if (leadStatus === "ETB" && showTradeNow) {
      setButtonText(tradeNowText ?? "TRADE NOW");
    } else {
      setButtonText(openDematButtonText ?? "OPEN DEMAT ACCOUNT");
    }
  }, [
    leadStatus,
    showTradeNow,
    tradeNowText,
    openDematButtonText,
    resumeJourneyText,
  ]);

  const isDematOnboardingFlow =
    leadStatus === "" || leadStatus === "null" || leadStatus === "Inprogress";

  const utmSources = { utmSourceApp, utmSourceWeb };

  // BRD: Open Demat / Resume Journey skip OTP.
  // Consent given or Continue → encrypted BFSL redirect with req payload.
  const redirectToBfslWithEncryptedPayload = async (
    mobileOverride?: string,
  ) => {
    try {
      const redirectUrl = await buildBfslEncryptedRedirectUrl(
        mobileOverride ?? sessionMobileNo,
        bfslPwaBaseUrl,
      );
      redirectWithUtmParams(redirectUrl, "false", utmSources);
    } catch (error) {
      console.error("Error building BFSL encrypted redirect URL:", error);
    }
  };

  const shouldOpenModal = useMemo(() => {
    return companies.length > 1 && companies.length < 4;
  }, [companies]);

  const trackCtaGroupEvent = (
    ctaText: string,
    submissionStatus: string = "",
  ) => {
    void trackDematCtaGroupEvent(headerName, ctaText, submissionStatus);
  };

  return (
    <>
      <Conditional
        if={
          (isAuthSuccessOrPerpetual && leadStatus === null) ||
          loginStatus === "pending"
        }
      >
        <BFLSkeleton width={225} count={1} height={40} />
      </Conditional>

      <Conditional
        if={
          (isAuthSuccessOrPerpetual &&
            (leadStatus === "" ||
              leadStatus === "null" ||
              leadStatus === "Inprogress")) ||
          loginStatus === "failure"
        }
      >
        <OpenDematButton
          showArrow={showArrow}
          openDematButtonText={buttonText}
          onClick={() => {
            const effectiveCtaText =
              buttonText ?? openDematButtonText ?? "OPEN DEMAT ACCOUNT";
            if (showUnifiedCt && onMasterCtaClick) {
              onMasterCtaClick(effectiveCtaText);
            } else {
              handleDematAccountDL(
                loginStatus,
                effectiveCtaText,
                compName ?? "Unknown Company",
              );
            }

            // For non-logged-in users, redirect immediately and return
            if (!isAuthSuccessOrPerpetual) {
              redirectWithUtmParams(BFSL_PWA_URL, "false", utmSources);
              return; // Exit early to prevent modal from opening
            }

            // Consent already given → encrypted redirect (skip OTP)
            if (isDematOnboardingFlow && consentProvided === true) {
              redirectToBfslWithEncryptedPayload();
              return;
            }

            if (!consentProvided) {
              setOpenModal("consent");
              if (showUnifiedCt) {
                trackAuthenticationConsentDrawerClick(
                  effectiveCtaText,
                  "consent_modal_opened",
                );
              } else {
                handleViewDL("REDIRECTING TO BFSL");
              }
            }
          }}
        />
      </Conditional>

      <Conditional
        if={
          isAuthSuccess &&
          leadStatus === "ETB" &&
          showTradeNow === false &&
          showBuySellButtons === true
        }
      >
        <div
          className={`flex items-center space-x-2 ${(showCtaPadding ?? true) ? "py-[10px]" : "py-0"} ${isFloating && leadStatus === "ETB" ? "fixed bottom-0 left-0 h-[72px] w-full justify-center bg-white pl-4 pr-4 shadow-[-4px_-4px_6px_#00000014]" : "between-center"}`}
        >
          <button
            className={`cursor-pointer rounded-full bg-[#00A989] px-10 py-2 text-sm font-medium text-white ${isFloating && leadStatus === "ETB" ? "mr-[10px] h-[40px] w-[167px]" : ""}`}
            onClick={() => {
              // Track button click
              if (shouldOpenModal) {
                trackCtaGroupEvent(buyText ?? "BUY");
                setOpenCompanyBuySellModal(true);
                return;
              }
              if (showUnifiedCt) {
                trackCtaGroupEvent(buyText ?? "BUY");
              } else {
                handleClickDL("BUY", formattedSymbol);
              }
              handleSSORedirect(
                "buy",
                loginStatus,
                exchId,
                formattedSymbol,
                nseToken,
                bseToken,
                headerName,
              );
            }}
            data-testid="buy-button"
          >
            {buyText ?? "BUY"}
          </button>
          <button
            className={`cursor-pointer rounded-full bg-[#E85B0F] px-10 py-2 text-sm font-medium text-white ${isFloating && leadStatus === "ETB" ? "h-[40px] w-[167px]" : ""}`}
            onClick={() => {
              // Track button click
              if (shouldOpenModal) {
                trackCtaGroupEvent(sellText ?? "SELL");
                setOpenCompanyBuySellModal(true);
                return;
              }
              if (showUnifiedCt) {
                trackCtaGroupEvent(sellText ?? "SELL");
              } else {
                handleClickDL("SELL", formattedSymbol);
              }
              handleSSORedirect(
                "sell",
                loginStatus,
                exchId,
                formattedSymbol,
                nseToken,
                bseToken,
                headerName,
              );
            }}
            data-testid="sell-button"
          >
            {sellText ?? "SELL"}
          </button>
        </div>
      </Conditional>

      <Conditional
        if={
          isAuthSuccess &&
          leadStatus === "ETB" &&
          showTradeNow === true &&
          showBuySellButtons === false
        }
      >
        <OpenDematButton
          openDematButtonText={buttonText}
          onClick={() => {
            const effectiveCtaText = buttonText ?? tradeNowText ?? "TRADE NOW";
            if (showUnifiedCt && onMasterCtaClick) {
              onMasterCtaClick(effectiveCtaText);
            } else {
              handleDematAccountDL(
                loginStatus,
                effectiveCtaText,
                compName ?? "",
              );
            }
            handleClickDL("TRADE NOW", formattedSymbol);

            handleTradeNowRedirect(loginStatus, headerName);
          }}
        />
      </Conditional>

      {/* Consent Modal */}
      <Conditional if={openModal === "consent"}>
        <BFLModal
          data-testid="bfl-modal"
          isOpen={true}
          onClose={() => {
            setOpenModal("none");
            if (showUnifiedCt) {
              void trackAuthenticationConsentDrawerClick(
                authenticationConsentCrossIconCtaText,
                "",
                authenticationConsentCrossIconInteraction,
              );
            } else {
              handleClickDL("CLOSE", "REDIRECTING TO BFSL");
            }
          }}
          title="Redirecting you to Bajaj Financial Securities Limited"
          secondaryButtonText="Skip"
          onSecondaryButtonClick={() => {
            setOpenModal("none");
            if (showUnifiedCt) {
              void trackAuthenticationConsentDrawerClick("Skip", "skip");
            } else {
              handleClickDL("Skip", "REDIRECTING TO BFSL");
            }
            // Skip → plain BFSL redirect without mobile / encrypted req
            redirectWithUtmParams(BFSL_PWA_URL, "false", utmSources);
          }}
          onPrimaryButtonClick={async () => {
            try {
              // Call consent insert API
              const consentResponse = await submitConsentInsert();

              // Check if consent was successful
              if (
                consentResponse.statusCode === 90 &&
                consentResponse.status === "success"
              ) {
                setConsentProvided(true);
                const consentMobileNumber = consentResponse.data?.mobileNumber;
                if (consentMobileNumber) {
                  setSessionMobileNo(consentMobileNumber);
                }

                setOpenModal("none");
                if (showUnifiedCt) {
                  trackAuthenticationConsentDrawerClick("Continue", "continue");
                } else {
                  handleClickDL("Continue", "REDIRECTING TO BFSL");
                }
                // Continue → encrypted BFSL redirect with mobileNo in payload
                redirectToBfslWithEncryptedPayload(consentMobileNumber);
              } else {
                console.error("Consent API failed:", consentResponse.message);
                setConsentProvided(false);
                // Handle error case - maybe show error message or keep modal open
              }
            } catch (error) {
              // Handle error case
              console.error("Error in consent submission:", error);
              setConsentProvided(false);
            }
          }}
          enableScroll={false}
          buttonText="Continue"
        >
          <DematConsentModal />
        </BFLModal>
      </Conditional>

      {/* Company Buy Sell Modal */}
      <Conditional if={openCompanyBuySellModal}>
        <CompanyBuySellModal
          isOpen={openCompanyBuySellModal}
          onClose={() => {
            setOpenCompanyBuySellModal(false);
          }}
          companies={companies}
          headerName={headerName}
          buyText={buyText ?? "BUY"}
          sellText={sellText ?? "SELL"}
          title={modalTitle}
        />
      </Conditional>
    </>
  );
};

export default DematAccount;
